import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  HostListener,
  inject,
  OnDestroy,
  signal,
  Signal,
  viewChild,
  WritableSignal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  EEntityType,
  ERelationType,
  ICorporateDesign,
  IFont,
  IImage,
  ITextLanguageValue,
} from '@web/dtos';
import { ImageSelectComponent } from '../../../components/data-input/form-inputs/image-select/image-select.component';
import { ButtonComponent } from '../../../components/feedback/button/button.component';
import { LoadingAnimationComponent } from '../../../components/feedback/loading-animation/loading-animation.component';
import { BannerStore } from '../../../services/api/banner/banner-store.service';
import { CorporateDesignStore } from '../../../services/api/corporate-design/corporate-design-store.service';
import { FontStore } from '../../../services/api/font/font-store.service';
import { FooterStore } from '../../../services/api/footer/footer-store.service';
import { HeaderStore } from '../../../services/api/header/header-store.service';
import { ImageStore } from '../../../services/api/image/image-store.service';
import { CmsTextProviderService } from '../../../services/cms-text-provider.service';
import { UnfoldComponent } from '../../../components/feedback/unfold-button/unfold.component';
import { AppearanceStandardControlsComponent } from '../../../components/appearance-standard-controls/appearance-standard-controls.component';
import { AppearanceHighlightedActionsComponent } from '../../../components/appearance-highlighted-actions/appearance-highlighted-actions.component';
import { AppConfigService } from '../../../services/app-config.service';
import { CorporateDesignFormsFactory } from '../../../factory/corporateDesign/corporateDesign-forms-factory.service';
import { ICorporateDesignForm } from '../../../entities/coporate-design-entity';
import { TranslationTextInputComponent } from '../../../components/data-input/form-inputs/translation-text-input/translation-text-input.component';
import { BrowserTabPreviewComponent } from '../../../components/browser-tab-preview/browser-tab-preview.component';

@Component({
  selector: 'aw-appearance-page',
  imports: [
    AppearanceHighlightedActionsComponent,
    AppearanceStandardControlsComponent,
    BrowserTabPreviewComponent,
    ButtonComponent,
    ImageSelectComponent,
    LoadingAnimationComponent,
    ReactiveFormsModule,
    TranslationTextInputComponent,
    UnfoldComponent,
  ],
  templateUrl: './appearance-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
})
export class AppearancePageComponent implements OnDestroy {
  private readonly appConfigService: AppConfigService =
    inject(AppConfigService);
  private readonly bannerStore: BannerStore = inject(BannerStore);
  private readonly corporateDesignStore: CorporateDesignStore =
    inject(CorporateDesignStore);
  private readonly corporateDesignFormsFactory: CorporateDesignFormsFactory =
    inject(CorporateDesignFormsFactory);
  private readonly fontStore: FontStore = inject(FontStore);
  private readonly footerStore: FooterStore = inject(FooterStore);
  private readonly headerStore: HeaderStore = inject(HeaderStore);
  protected readonly imageStore: ImageStore = inject(ImageStore);
  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );

  private readonly unsavedChanges: WritableSignal<boolean> = signal(false);
  private readonly highlightedActions: Signal<
    AppearanceHighlightedActionsComponent | undefined
  > = viewChild(AppearanceHighlightedActionsComponent);
  protected readonly sectionsCollapsed: WritableSignal<boolean> = signal(false);
  protected readonly EntityType: typeof EEntityType = EEntityType;
  protected readonly TextRelationType: typeof ERelationType = ERelationType;
  protected readonly corporateDesignForm: FormGroup<ICorporateDesignForm> =
    this.corporateDesignFormsFactory.buildForm();
  private initialFavicon: IImage | null = null;
  private uploadedFaviconFile: File | null = null;
  private faviconPreviewObjectUrl: string | null = null;
  private faviconInitialized: boolean;
  private designInitialized: boolean = false;
  protected readonly faviconControl: FormControl<IImage | null> =
    new FormControl<IImage | null>(null);
  protected readonly faviconPreviewUrl: WritableSignal<string> = signal('');
  protected readonly isLoading: Signal<boolean> = computed(
    (): boolean =>
      this.bannerStore.isLoading() ||
      this.corporateDesignStore.isLoading() ||
      this.fontStore.isLoading() ||
      this.footerStore.isLoading() ||
      this.headerStore.isLoading() ||
      this.imageStore.isLoading()
  );

  constructor() {
    this.faviconInitialized = false;

    effect((): void => {
      const corporateDesign: ICorporateDesign =
        this.corporateDesignStore.corporateDesign();
      if (this.designInitialized || this.corporateDesignStore.isLoading()) {
        return;
      }
      this.corporateDesignForm.reset(corporateDesign, { emitEvent: false });
      this.designInitialized = true;
    });

    effect((): void => {
      const images: IImage[] = this.imageStore.images();
      const corporateDesign = this.corporateDesignStore.corporateDesign();
      if (
        this.faviconInitialized ||
        this.imageStore.isLoading() ||
        this.corporateDesignStore.isLoading() ||
        images.length === 0
      ) {
        return;
      }

      const icoImages: IImage[] = images.filter(
        (image: IImage): boolean =>
          image.fileExtension.toLocaleLowerCase() === 'ico'
      );
      if (icoImages.length === 0) {
        this.faviconInitialized = true;
        return;
      }

      const corporateFaviconFilename: string =
        corporateDesign.faviconFilename.replace(/\.$/, '');
      const favicon: IImage =
        icoImages.find(
          (image: IImage): boolean =>
            image.filename === corporateFaviconFilename &&
            image.fileExtension.toLocaleLowerCase() ===
              corporateDesign.faviconFileExtension.toLocaleLowerCase()
        ) ??
        icoImages.find((image: IImage): boolean =>
          image.filename.toLocaleLowerCase().includes('favicon')
        ) ??
        icoImages[0];
      this.initialFavicon = favicon;
      this.faviconControl.setValue(favicon, { emitEvent: false });
      this.updateFaviconPreview(favicon);
      this.faviconInitialized = true;
    });

    this.faviconControl.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((favicon: IImage | null): void => {
        this.updateFaviconPreview(favicon);
        this.setHasUnsavedChanges(true);
      });
    this.corporateDesignForm.valueChanges
      .pipe(takeUntilDestroyed())
      .subscribe((): void => this.setHasUnsavedChanges(true));
  }

  public hasUnsavedChanges(): boolean {
    return this.unsavedChanges();
  }

  public setHasUnsavedChanges(hasUnsavedChanges: boolean): void {
    this.unsavedChanges.set(hasUnsavedChanges);
  }

  protected async save(): Promise<boolean> {
    if (this.corporateDesignForm.invalid) {
      this.corporateDesignForm.markAllAsTouched();
      return false;
    }
    const favicon: IImage | null = this.faviconControl.value;
    const previousDesign: ICorporateDesign =
      this.corporateDesignStore.corporateDesign();
    const corporateDesign: ICorporateDesign = {
      ...previousDesign,
      ...this.corporateDesignForm.getRawValue(),
    } as ICorporateDesign;
    const pendingFontFile: File | null =
      this.highlightedActions()?.getPendingFontFile() ?? null;

    if (pendingFontFile) {
      const formData: FormData = new FormData();
      formData.append('font', pendingFontFile, pendingFontFile.name);
      const knownFontIds: Set<string | undefined> = new Set(
        this.fontStore.fonts().map((font: IFont): string | undefined => font.id)
      );
      const fonts: IFont[] = await this.fontStore.upload(formData);
      corporateDesign.font =
        fonts.find((font: IFont): boolean => !knownFontIds.has(font.id)) ??
        fonts.find(
          (font: IFont): boolean => font.name === pendingFontFile.name
        ) ??
        previousDesign.font;
    } else if (this.highlightedActions()?.isDefaultFontSelected()) {
      corporateDesign.font = null;
    }

    if (favicon?.id && favicon.fileExtension.toLocaleLowerCase() === 'ico') {
      const filename = `${favicon.filename}.${favicon.fileExtension}`;
      const file: File =
        this.uploadedFaviconFile?.name === filename
          ? this.uploadedFaviconFile
          : new File([await this.imageStore.getFile(favicon.id)], filename, {
              type: 'image/x-icon',
            });
      corporateDesign.faviconFilename = favicon.filename;
      corporateDesign.faviconFileExtension = favicon.fileExtension;
      const formData: FormData = new FormData();
      formData.append('favicon', file, filename);
      formData.append('corporateDesign', JSON.stringify(corporateDesign));
      const savedCorporateDesign: ICorporateDesign | null =
        await this.corporateDesignStore.upload(formData);
      if (!savedCorporateDesign) {
        return false;
      }
      this.updateBrowserFavicon();
    } else {
      const savedCorporateDesign: ICorporateDesign | null =
        await this.corporateDesignStore.save(corporateDesign);
      if (!savedCorporateDesign) {
        return false;
      }
    }

    this.corporateDesignForm.reset(
      this.corporateDesignStore.corporateDesign(),
      { emitEvent: false }
    );
    this.updateBrowserTitle(this.corporateDesignStore.corporateDesign());
    this.highlightedActions()?.commitSavedFont(corporateDesign.font);
    this.initialFavicon = favicon;
    this.uploadedFaviconFile = null;
    this.updateFaviconPreview(favicon);
    this.setHasUnsavedChanges(false);
    return true;
  }

  protected cancel(): void {
    this.corporateDesignForm.reset(
      this.corporateDesignStore.corporateDesign(),
      { emitEvent: false }
    );
    this.faviconControl.setValue(this.initialFavicon, { emitEvent: false });
    this.updateFaviconPreview(this.initialFavicon);
    this.highlightedActions()?.resetFontDraft();
    this.uploadedFaviconFile = null;
    this.setHasUnsavedChanges(false);
  }

  protected async uploadFavicon(event: Event): Promise<void> {
    const input: HTMLInputElement = event.target as HTMLInputElement;
    const file: File | undefined = input.files?.[0];
    if (!file) {
      return;
    }
    if (file.name.split('.').pop()?.toLocaleLowerCase() !== 'ico') {
      input.value = '';
      return;
    }

    const faviconFile: File = new File([file], file.name, {
      type: 'image/x-icon',
    });
    const formData: FormData = new FormData();
    formData.append('newImages', faviconFile, faviconFile.name);
    formData.append('images', JSON.stringify(this.imageStore.images()));
    await this.imageStore.upload(formData);

    const lastDot: number = faviconFile.name.lastIndexOf('.');
    const filename: string =
      lastDot > 0 ? faviconFile.name.slice(0, lastDot) : faviconFile.name;
    const uploadedImage: IImage | undefined = this.imageStore
      .images()
      .find(
        (image: IImage): boolean =>
          image.filename === filename &&
          image.fileExtension.toLocaleLowerCase() === 'ico'
      );

    if (uploadedImage) {
      this.uploadedFaviconFile = faviconFile;
      this.faviconControl.setValue(uploadedImage);
    }
    input.value = '';
  }

  private updateBrowserFavicon(): void {
    document.head
      .querySelectorAll<HTMLLinkElement>('link[rel~="icon"]')
      .forEach((link: HTMLLinkElement): void => link.remove());

    const faviconLink: HTMLLinkElement = document.createElement('link');
    faviconLink.rel = 'icon';
    faviconLink.type = 'image/x-icon';
    faviconLink.href = `${this.appConfigService.get().general.cmsUrl}/corporate-design/favicon?v=${Date.now()}`;
    document.head.appendChild(faviconLink);
  }

  private updateBrowserTitle(corporateDesign: ICorporateDesign): void {
    const title: string = this.resolveBrowserTabTitle(corporateDesign.tabs);
    if (title) {
      document.title = title;
    }
  }

  protected browserTabPreviewTitle(): string {
    return this.resolveBrowserTabTitle(
      this.corporateDesignForm.controls.tabs.value
    );
  }

  private resolveBrowserTabTitle(tabs: ICorporateDesign['tabs']): string {
    const values: ITextLanguageValue[] = tabs.flatMap(tab => tab.text.values);
    return (
      values.find(
        (value: ITextLanguageValue): boolean =>
          value.languagePrefix.toLocaleUpperCase() === 'DE'
      )?.value ??
      values[0]?.value ??
      'Webshop'
    );
  }

  private updateFaviconPreview(favicon: IImage | null): void {
    if (this.faviconPreviewObjectUrl) {
      URL.revokeObjectURL(this.faviconPreviewObjectUrl);
      this.faviconPreviewObjectUrl = null;
    }

    if (favicon?.id) {
      this.faviconPreviewUrl.set(this.imageStore.getImagePath(favicon.id));
      return;
    }

    if (favicon && this.uploadedFaviconFile) {
      this.faviconPreviewObjectUrl = URL.createObjectURL(
        this.uploadedFaviconFile
      );
      this.faviconPreviewUrl.set(this.faviconPreviewObjectUrl);
      return;
    }

    this.faviconPreviewUrl.set('');
  }

  public ngOnDestroy(): void {
    if (this.faviconPreviewObjectUrl) {
      URL.revokeObjectURL(this.faviconPreviewObjectUrl);
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  protected preventNavigationWithUnsavedChanges(
    event: BeforeUnloadEvent
  ): void {
    if (this.hasUnsavedChanges()) {
      event.preventDefault();
    }
  }
}
